import { useMemo, useState } from 'react'

import { useFacilitySearch } from './useFacilitySearch'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'use-debounce'

import { FacilityFilterInput } from '@/graphql/generated/schemas'
import {
  FacilityOptionWithCueI,
  getFacilityOptionsWithMatchedByAddressCue,
  getIncidentFacilityOptionsWithAddress,
} from '@/utils/forms/optionBuilders'
import { SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

interface UseFacilityFilterOptionsIProps {
  filter?: FacilityFilterInput
}

interface UseFacilityFilterOptionsResultIProps {
  facilityOptions: FacilityOptionWithCueI[]
  isFacilitiesLoading: boolean
  isFacilitiesError: boolean
  facilitySearch: string
  setFacilitySearch: (value: string) => void
}

/**
 * Debounced facility search + "matched by address" cue, shared by the dark
 * filter drawers (Devices, Rules, Guard Tours) that searched name/short
 * name/address and tagged the result identically — this collapses what was
 * duplicated verbatim across all three. The Incidents filter drawer has
 * extra enrichment-merge and short-query local-narrowing logic on top of
 * this same pattern, so it stays on its own hook rather than being forced in.
 */
export const useFacilityFilterOptions = ({
  filter,
}: UseFacilityFilterOptionsIProps = {}): UseFacilityFilterOptionsResultIProps => {
  const { t } = useTranslation()
  const [facilitySearch, setFacilitySearch] = useState('')
  const [debouncedFacilitySearch] = useDebounce(
    facilitySearch,
    SEARCH_DEBOUNCE_TIME
  )
  const {
    facilitiesData,
    isFacilitiesLoading,
    isError: isFacilitiesError,
  } = useFacilitySearch({ searchQuery: debouncedFacilitySearch, filter })

  const facilityOptionsFromSearch = useMemo(
    () =>
      getIncidentFacilityOptionsWithAddress(
        facilitiesData,
        true,
        debouncedFacilitySearch
      ),
    [facilitiesData, debouncedFacilitySearch]
  )

  const facilityOptions = useMemo(
    () =>
      getFacilityOptionsWithMatchedByAddressCue(
        facilityOptionsFromSearch,
        t('common:facility_matched_by_address')
      ),
    [facilityOptionsFromSearch, t]
  )

  return {
    facilityOptions,
    isFacilitiesLoading,
    isFacilitiesError,
    facilitySearch,
    setFacilitySearch,
  }
}
